import { Controller } from '@hotwired/stimulus'
import {
  OUTCOMES_CHANGE_HEADING,
  OUTCOMES_NO_LONGER_OFFERED,
  OUTCOMES_RESOLVED_KEEP_MANY,
  OUTCOMES_RESOLVED_KEEP_ONE,
  OUTCOMES_WILL_BE_OFFERED,
  REMOVE_OUTCOME,
  trans,
} from '../translator'

export default class OutcomesPreviewController extends Controller<HTMLElement> {
  static targets = ['enabled', 'wrap', 'list', 'newOutcome', 'newOutcomeError', 'consequence', 'save']
  static values = { enabled: Boolean, resolved: Number }

  declare readonly enabledTarget: HTMLInputElement
  declare readonly wrapTarget: HTMLElement
  declare readonly hasWrapTarget: boolean
  declare readonly listTarget: HTMLElement
  declare readonly hasListTarget: boolean
  declare readonly newOutcomeTarget: HTMLInputElement
  declare readonly hasNewOutcomeTarget: boolean
  declare readonly newOutcomeErrorTarget: HTMLElement
  declare readonly hasNewOutcomeErrorTarget: boolean
  declare readonly consequenceTarget: HTMLElement
  declare readonly hasConsequenceTarget: boolean
  declare readonly saveTarget: HTMLButtonElement
  declare readonly hasSaveTarget: boolean
  declare readonly enabledValue: boolean
  declare readonly resolvedValue: number

  private listChanged = false

  private readonly onChange = (): void => this.refresh()

  connect (): void {
    this.element.addEventListener('change', this.onChange)
    this.refresh()
  }

  disconnect (): void {
    this.element.removeEventListener('change', this.onChange)
  }

  add (event: Event): void {
    event.preventDefault()

    if (!this.hasNewOutcomeTarget || !this.hasListTarget) {
      return
    }

    const label = this.newOutcomeTarget.value.trim()

    if (label === '') {
      return
    }

    if (this.isDuplicate(label)) {
      this.showDuplicateError()
      return
    }

    this.clearError()
    this.listTarget.querySelector('.ft-outcomes__empty')?.remove()
    this.listTarget.appendChild(this.buildItem(label))
    this.newOutcomeTarget.value = ''

    this.listChanged = true
    this.refresh()
  }

  remove (event: Event): void {
    (event.currentTarget as HTMLElement).closest('li')?.remove()

    this.listChanged = true
    this.refresh()
  }

  clearError (): void {
    this.newOutcomeTarget.classList.remove('is-invalid')

    if (this.hasNewOutcomeErrorTarget) {
      this.newOutcomeErrorTarget.hidden = true
    }
  }

  private showDuplicateError (): void {
    this.newOutcomeTarget.classList.add('is-invalid')

    if (this.hasNewOutcomeErrorTarget) {
      this.newOutcomeErrorTarget.hidden = false
    }
  }

  private isDuplicate (label: string): boolean {
    if (!this.hasListTarget) {
      return false
    }

    return Array.from(this.listTarget.querySelectorAll<HTMLInputElement>('input[name="outcomes[]"]'))
      .some((input) => input.value.toLowerCase() === label.toLowerCase())
  }

  private buildItem (label: string): HTMLLIElement {
    const item = document.createElement('li')

    const span = document.createElement('span')
    span.textContent = label

    const hidden = document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = 'outcomes[]'
    hidden.value = label

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'ft-outcome-x'
    button.setAttribute('aria-label', trans(REMOVE_OUTCOME, { '%label%': label }, 'form-types'))
    button.title = trans(REMOVE_OUTCOME, { '%label%': label }, 'form-types')
    button.setAttribute('data-action', 'outcomes-preview#remove')

    const icon = document.createElement('i')
    icon.className = 'fas fa-times'
    icon.setAttribute('aria-hidden', 'true')
    button.appendChild(icon)

    item.appendChild(span)
    item.appendChild(hidden)
    item.appendChild(button)

    return item
  }

  refresh (): void {
    const enabled = this.enabledTarget.checked

    if (this.hasWrapTarget) {
      this.wrapTarget.hidden = !enabled
    }

    const switchChanged = enabled !== this.enabledValue

    this.render(enabled, switchChanged)

    if (this.hasSaveTarget) {
      this.saveTarget.disabled = !switchChanged && !this.listChanged
    }
  }

  private render (enabled: boolean, changed: boolean): void {
    if (!this.hasConsequenceTarget) {
      return
    }

    this.consequenceTarget.textContent = ''

    if (!changed) {
      return
    }

    const heading = document.createElement('p')
    heading.textContent = trans(OUTCOMES_CHANGE_HEADING, {}, 'form-types')

    const list = document.createElement('ul')

    for (const bit of enabled ? [trans(OUTCOMES_WILL_BE_OFFERED, {}, 'form-types')] : this.turningOffBits()) {
      const item = document.createElement('li')
      item.textContent = bit
      list.appendChild(item)
    }

    this.consequenceTarget.appendChild(heading)
    this.consequenceTarget.appendChild(list)
  }

  private turningOffBits (): string[] {
    const bits = [trans(OUTCOMES_NO_LONGER_OFFERED, {}, 'form-types')]

    if (this.resolvedValue === 1) {
      bits.push(trans(OUTCOMES_RESOLVED_KEEP_ONE, {}, 'form-types'))
    } else if (this.resolvedValue > 1) {
      bits.push(trans(OUTCOMES_RESOLVED_KEEP_MANY, { '%count%': String(this.resolvedValue) }, 'form-types'))
    }

    return bits
  }
}
